import { useEffect, useMemo, useState } from "react";
import { Reveal } from "@/components/Reveal";
import PostCard from "@/components/PostCard";
import api from "@/lib/api";

export default function Research() {
  const [posts, setPosts] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/posts", { params: { type: "paper" } })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  const cats = useMemo(() => ["All", ...new Set((posts || []).map((p) => p.category))], [posts]);
  const filtered = (posts || []).filter((p) => {
    const inCat = cat === "All" || p.category === cat;
    const q = query.toLowerCase();
    const inQuery =
      !q ||
      p.title.toLowerCase().includes(q) ||
      (p.excerpt || "").toLowerCase().includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q));
    return inCat && inQuery;
  });

  return (
    <main className="pt-28 lg:pt-36">
      <section className="wrap">
        <Reveal>
          <p className="overline-mono">Research & Papers</p>
          <h1 className="max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Studies, frameworks & diagnostics.
          </h1>
          <p data-testid="research-count" className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy-mute">
            {posts === null ? "Cataloguing…" : `${String(filtered.length).padStart(2, "0")} papers in the catalog`}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col gap-6 border-y border-line py-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  data-testid={`research-filter-button-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    cat === c ? "border-blue bg-blue text-white" : "border-line text-navy-soft hover:border-blue hover:text-blue"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              data-testid="research-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles, excerpts, tags…"
              className="w-full border border-line bg-white px-4 py-3 text-sm text-navy placeholder:text-navy-mute transition-colors focus:border-blue focus:outline-none lg:w-72"
            />
          </div>
        </Reveal>

        {posts !== null && filtered.length === 0 && (
          <p className="py-24 text-center font-serif italic text-2xl text-navy-mute">
            Nothing in this filter yet — the desk is still writing.
          </p>
        )}

        <div className="grid gap-6 py-16 md:grid-cols-2 lg:grid-cols-3 lg:py-20">
          {filtered.map((p, i) => (
            <PostCard key={p.slug} post={p} index={i} />
          ))}
        </div>
      </section>
    </main>
  );
}